import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Box, Center, Grid, Text } from '@wegroup/design-system';
import { useDispatch } from 'react-redux';
import { LoadingSpinner } from 'wg-fe-ui';
import {
  getFlatProductsResponse,
  useTopProducts,
} from '../../../../../hooks/useQuoteQueries';
import { useLouiseSelector } from '../../../../../store/redux/store';
import {
  setActiveCarQuoteFilter,
  setShownProductsIds,
} from '../../../../../store/redux/slices/offersSlice';
import { groupByInsuranceCompanyForId } from '../../../../../services/productFactoryService';
import { isSocialLoginUrl } from '../../../../../../../app/src/features/authentication/constants/SocialAuth';
import { ProductWithQuality } from '../../../../../../../../types/Quotes';
import ErrorPage from '../../../../../../../common/src/components/ErrorPage';
import OffersContainerContent from '../../../../../components/offers/OffersContainerContent';
import { mq } from '../../../../../../../common/src/utils/styledComponentsUtils';
import Avatar from '../../../../../components/default/ProdFacAvatar';
import OffersContainerHeader from './OffersOverviewHeader';
import OffersBottomBar from './OffersBottomBar';
import useMobileBreakpoints from '../../../../../../../common/src/hooks/useMobileBreakpoints';
import ComparisonBar from '../../../../comparison/components/comparison-bar/ComparisonBar';
import CompareOffersHoverButton from '../../../../comparison/components/CompareOffersHoverButton';

interface Props {
  riskObject: string;
  isProductsLoading?: boolean;
  setIsComparisonActive: (arg0: boolean) => void;
  addToSelectedOffers: (arg0: string) => void;
  removeFromSelectedOffers: (id?: string) => void;
}

const OffersOverviewContainer: React.FC<React.PropsWithChildren<Props>> = ({
  riskObject,
  isProductsLoading,
  setIsComparisonActive,
  addToSelectedOffers,
  removeFromSelectedOffers,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isLaptop } = useMobileBreakpoints();

  if (!riskObject) return <ErrorPage />;

  const { data: products, isFetching, isError } = useTopProducts(riskObject);

  const shownProductIds = useLouiseSelector(
    (state) => state.offers.shownProductIds,
  );
  const activeCarQuoteFilter = useLouiseSelector(
    (state) => state.offers.activeCarQuoteFilter,
  );
  const selectedGuarantees = useLouiseSelector(
    (state) => state.selection.selectedGuarantees[riskObject],
  );
  const selectedCompanies = useLouiseSelector(
    (state) => state.selection.selectedCompanies,
  );
  const comparisonMatrixOffers = useLouiseSelector(
    (state) => state.offers.comparisonMatrixOffers,
  );

  const pageId = ['car', 'two_wheeler'].includes(riskObject)
    ? activeCarQuoteFilter
    : riskObject;

  useEffect(() => {
    if (
      products?.[riskObject] &&
      (!shownProductIds[pageId] || shownProductIds[pageId]?.length === 0)
    ) {
      dispatch(
        setShownProductsIds({
          pageId: pageId,
          productIds: groupByInsuranceCompanyForId(
            products,
            riskObject,
            selectedGuarantees,
            selectedCompanies,
            ['car', 'two_wheeler'].includes(riskObject)
              ? activeCarQuoteFilter
              : undefined,
          ),
        }),
      );
    }
  }, [products, activeCarQuoteFilter]);

  // If no omnium or mini omnium products are available, set the filter to civil liability
  // If no omnium products are available, set the filter to mini omnium
  // This makes sure the user can always select a product
  useEffect(() => {
    if (
      !activeCarQuoteFilter &&
      ['car', 'two_wheeler'].includes(riskObject) &&
      products
    ) {
      const omniumProducts =
        products?.[riskObject].filter(({ info }) =>
          info.insurance_name.includes('omnium'),
        ) || [];
      const miniOmniumProductst =
        products?.[riskObject].filter(({ info }) =>
          info.insurance_name.includes('mini_omnium'),
        ) || [];

      if (
        (selectedGuarantees?.includes('OMNIUM') || isSocialLoginUrl) &&
        omniumProducts.length > 0
      )
        dispatch(setActiveCarQuoteFilter('omnium'));
      else if (
        (selectedGuarantees?.includes('MINI_OMNIUM') || isSocialLoginUrl) &&
        miniOmniumProductst.length > 0
      )
        dispatch(setActiveCarQuoteFilter('mini_omnium'));
      else dispatch(setActiveCarQuoteFilter('civil_liability'));
    }
  }, [products]);

  const shownProductsList = shownProductIds[pageId]
    ?.map((hashId) =>
      getFlatProductsResponse(products).find(
        ({ hash_id }) => hash_id === hashId,
      ),
    )
    .filter((product) => product != null) as ProductWithQuality[];

  // If done fetching show error
  if (isError && !isFetching) return <ErrorPage />;

  if (isFetching || isProductsLoading || !shownProductsList || !products)
    return (
      <Center h="100%" flexDir="column">
        <LoadingSpinner />
        <Text mt="4" textStyle={{ base: 'h6Headline', laptop: 'h4Headline' }}>
          {t('Retrieving offers')}
        </Text>
      </Center>
    );

  return (
    <>
      <Box h={{ laptop: '100%' }} overflowY={{ laptop: 'auto' }}>
        <Grid
          w="100%"
          margin="0 auto"
          gridGap="6"
          gridTemplateColumns={{
            base: '1fr',
            laptopL: '7.6rem minmax(auto, 100rem)',
          }}
          maxW="6xl"
          paddingX={{ base: '6', laptop: '14' }}
          paddingY={{ base: '4', laptop: '6' }}
        >
          <Box display={{ laptop: 'none' }}>
            {comparisonMatrixOffers[riskObject]?.length > 0 && (
              <CompareOffersHoverButton
                onClick={() => setIsComparisonActive(true)}
              />
            )}
          </Box>
          <StyledAvatar />
          <div>
            <OffersContainerHeader
              riskObject={riskObject}
              riskObjectErrors={products.risk_object_errors}
            />

            <OffersContainerContent
              shownProductsList={shownProductsList}
              riskObject={riskObject}
              addToSelectedOffers={addToSelectedOffers}
              removeFromSelectedOffers={removeFromSelectedOffers}
            />
          </div>
        </Grid>
        {comparisonMatrixOffers[riskObject]?.length > 0 && isLaptop && (
          <Box pos="sticky" bottom="0" zIndex="3" w="100%">
            <ComparisonBar
              riskObject={riskObject}
              setIsComparisonActive={setIsComparisonActive}
            />
          </Box>
        )}
      </Box>
      <OffersBottomBar riskObject={riskObject} />
    </>
  );
};

const StyledAvatar = styled(Avatar)`
  display: none;

  ${mq.laptopL} {
    display: block;
    width: 7.6rem;
    height: 7.6rem;
  }
`;

export default OffersOverviewContainer;
